import React, { ReactNode, useCallback, useEffect, useState, useRef, useMemo } from 'react';
import Icon from '../../../../Icon2/Icon';
import Gallery, { ApiAsset } from '../../../../Gallery/Gallery';
import Heading from '../../../../Typography/Heading/Heading';
import Paragraph from '../../../../Typography/Paragraph/Paragraph';
import { isEmpty } from 'lodash';
import AssetInfoSidebar from './Sidebar';
import { fetchSpaceAssetsData } from '../viewHelper';
import { SpaceAssetsEmptyState } from '../EmptyState';
import {
  SPACE_ASSETS_VIEW_TYPE,
} from '../../../utils/constants';
import { AISuggestions, AssetData, AssetValidationFilter } from '../../../utils/types';
import { useQueryBuilder } from '../../SpaceAssetsSelectorModal/context/QueryBuilderContext';
import {
  filterNonDirectoryAssets,
  createBreadcrumbFromAsset,
  UI_CONSTANTS,
  generateContainerClassName,
  getSpaceUids,
} from './utils';
import './GalleryView.style.scss';
import { extractTextFromJSX } from '../../SpaceAssetsSelectorModal/helper';

const SelectionEmptyState = React.memo(() => (
  <div className="GalleryView__empty-state">
    <Heading className="GalleryView__empty-state-title" tagName="h2" text="No Asset Selected" />
    <Paragraph className="GalleryView__empty-state-subtitle" variant="p3" text="Select an asset to view details." />
  </div>
));

SelectionEmptyState.displayName = 'SelectionEmptyState';

interface GalleryViewProps {
  selectedFolder: AssetData | null;
  sortByData: { id: string; sortingDirection: string };
  spaceAssetsSearchOptions: { searchType: string | React.ReactNode; searchText: string };
  excludeUids: string[];
  ai_suggestions: AISuggestions;
  isAPIError: boolean;
  setIsAPIError: (isAPIError: boolean) => void;
  setShouldDisabledActionPanel: (shouldDisabledActionPanel: boolean) => void;
  linkedSpaces: string[];
  breadcrumbClickHandler: (crumbs: any) => void;
  setMessageComponent: (message: ReactNode) => void;
  handleGoBack: () => void;
  setShouldClearSearchFilters: (shouldClearSearchFilters: boolean) => void;
  isSuggestions: boolean;
  isMultiple: boolean;
  spaceAssetsPicked: AssetData[];
  setSpaceAssetsPicked: (assets: AssetData[]) => void;
  showSelectedAssets: boolean;
  linkedWorkspaces: { workspace: string, space_uid: string }[];
  isResponsive?: boolean;
  appliedFilterCount?: number;
  localesWithFallback?: any[];
  totalCounts: number;
  setTotalCounts: (totalCounts: number) => void;
  fetchSpaceAssets: (params: any, headers: any, options: any) => Promise<any>;
  validationFilter?: AssetValidationFilter;
  disablePrivateAssets?: boolean;
  spacesWithTitles?: Array<{ uid: string; title: string }>;
  onSuggestionsFetchSettled?: (success: boolean) => void;
  userDisplayNameMap?: Record<string, string>;
}

const GalleryView: React.FC<GalleryViewProps> = ({
  selectedFolder,
  sortByData,
  spaceAssetsSearchOptions,
  excludeUids,
  ai_suggestions,
  isAPIError,
  setIsAPIError,
  setShouldDisabledActionPanel,
  linkedSpaces,
  breadcrumbClickHandler,
  setMessageComponent,
  handleGoBack,
  setShouldClearSearchFilters,
  isSuggestions,
  isMultiple,
  spaceAssetsPicked,
  setSpaceAssetsPicked,
  showSelectedAssets,
  isResponsive = false,
  appliedFilterCount,
  localesWithFallback = [],
  linkedWorkspaces = [],
  totalCounts,
  setTotalCounts,
  fetchSpaceAssets,
  validationFilter,
  disablePrivateAssets = false,
  spacesWithTitles = [],
  onSuggestionsFetchSettled,
  userDisplayNameMap = {},
}) => {
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const [isSidebarExpanded, setSidebarExpanded] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [assetData, setAssetData] = useState<AssetData[]>([]);
  const [assetTypeSchemaData, setAssetTypeSchemaData] = useState<any>({});

  const [hasInitialLoad, setHasInitialLoad] = useState<boolean>(false);
  const [isInitialSetup, setIsInitialSetup] = useState<boolean>(true);

  const currentFolderUidRef = useRef<string | null>(null);
  const lastSearchParamsRef = useRef<string>('');

  const folderUid = useMemo((): string =>
    selectedFolder ? selectedFolder.uid : '',
    [selectedFolder]
  );

  const spaceUids = useMemo((): string[] =>
    getSpaceUids(selectedFolder, linkedSpaces),
    [selectedFolder, linkedSpaces]
  );

  const canLoadMore = useMemo((): boolean =>
    !loading && hasInitialLoad && assetData.length < totalCounts && totalCounts > 0,
    [loading, hasInitialLoad, assetData.length, totalCounts]
  );

  const { queryBuilder } = useQueryBuilder();

  const containerClassName = useMemo((): string =>
    generateContainerClassName(showSidebar, isSidebarExpanded, assetData.length > 0, loading),
    [showSidebar, isSidebarExpanded, assetData.length, loading]
  );


  const galleryAssets = useMemo(() => {
    const source = showSelectedAssets ? spaceAssetsPicked : assetData;
    return source as unknown as ApiAsset[];
  }, [assetData, spaceAssetsPicked, showSelectedAssets]);

  const gallerySelectedAssets = useMemo(
    () => spaceAssetsPicked as unknown as ApiAsset[],
    [spaceAssetsPicked],
  );

  const toggleSidebarExpansion = useCallback(() => {
    setSidebarExpanded(prev => !prev);
  }, []);

  const handleSelect = useCallback((selectedGalleryAssets: ApiAsset[]) => {
    const filteredAssets = filterNonDirectoryAssets(selectedGalleryAssets as unknown as AssetData[])
      .filter(asset => !asset.invalid_asset);
    setSpaceAssetsPicked(filteredAssets);
  }, [setSpaceAssetsPicked]);

  const handleAssetClick = useCallback((asset: ApiAsset) => {
    const assetData = asset as unknown as AssetData;
    
    if (assetData.invalid_asset) {
      return;
    }
    
    if (assetData.is_dir) {
      const crumbs = createBreadcrumbFromAsset(assetData);
      breadcrumbClickHandler(crumbs);
    } else {
      setSpaceAssetsPicked([assetData]);
    }
  }, [breadcrumbClickHandler, setSpaceAssetsPicked]);

  // One AbortController per query context. A fresh query (skip === 0) aborts the
  // previous one so in-flight Lottie sniffs cancel when the query/search changes;
  // pagination (skip > 0) reuses it so paging never cancels an earlier page's sniffs.
  const lottieAbortRef = useRef<AbortController | null>(null);

  const fetchAssets = useCallback((skip: number = 0, existingData: AssetData[] = []) => {
    if (showSelectedAssets) return;

    if (skip === 0) {
      lottieAbortRef.current?.abort();
      lottieAbortRef.current = new AbortController();
    }

    fetchSpaceAssetsData(
      queryBuilder,
      {
        sortBy: { id: sortByData.id, sortingDirection: sortByData.sortingDirection as 'asc' | 'desc' },
        searchText: spaceAssetsSearchOptions.searchText,
        searchType: extractTextFromJSX(spaceAssetsSearchOptions.searchType, 1),
        skip: skip,
        limit: UI_CONSTANTS.ITEMS_PER_PAGE,
        ai_suggestions
      },
      existingData,
      setLoading,
      setAssetData,
      setTotalCounts,
      setIsAPIError,
      setShouldDisabledActionPanel,
      fetchSpaceAssets,
      SPACE_ASSETS_VIEW_TYPE.GALLERY,
      linkedWorkspaces,
      null,
      setAssetTypeSchemaData,
      validationFilter,
      onSuggestionsFetchSettled,
      disablePrivateAssets,
      lottieAbortRef.current?.signal
    )
  }, [
    sortByData,
    excludeUids,
    ai_suggestions,
    setIsAPIError,
    setShouldDisabledActionPanel,
    assetData.length,
    showSelectedAssets,
    linkedWorkspaces,
    queryBuilder,
    validationFilter,
    onSuggestionsFetchSettled,
    disablePrivateAssets,
  ]);

  const handleLoadMore = useCallback(() => {
    if (!canLoadMore) return;

    const nextSkip = assetData.length;
    fetchAssets(nextSkip, assetData);
  }, [canLoadMore, assetData, fetchAssets]);

  useEffect(() => {
    const hasSelection = spaceAssetsPicked.length > 0;
    setShowSidebar(hasSelection);
    setSidebarExpanded(hasSelection);
  }, [spaceAssetsPicked.length]);

  useEffect(() => {
    setIsInitialSetup(true);
    // Cancel any in-flight Lottie sniff when the picker view unmounts (modal close).
    return () => lottieAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    setIsInitialSetup(false);

    setHasInitialLoad(false);

    fetchAssets(0, []);
  }, [isInitialSetup, ai_suggestions, queryBuilder]);

  useEffect(() => {
    if (assetData.length > 0 || (!loading && totalCounts === 0)) {
      setHasInitialLoad(true);
    }
  }, [assetData.length, loading, totalCounts]);
  return (
    <div className="GalleryView">
      <div className={containerClassName}>
        <div className="GalleryView__main-content">
          <Gallery
            assets={galleryAssets}
            selectedAssets={gallerySelectedAssets}
            loading={showSelectedAssets ? false : loading}
            onLoadMore={showSelectedAssets ? undefined : handleLoadMore}
            onSelect={handleSelect}
            onAssetClick={handleAssetClick}
            skip={showSelectedAssets ? 0 : assetData.length}
            limit={UI_CONSTANTS.ITEMS_PER_PAGE}
            totalCount={showSelectedAssets ? spaceAssetsPicked.length : totalCounts}
            selectable
            testId="gallery-view-gallery"
            className="GalleryView__gallery"
            noAssetsEmptyState={
              !loading &&
              <SpaceAssetsEmptyState
                searchApplied={!isEmpty(spaceAssetsSearchOptions.searchText) || appliedFilterCount! > 0}
                isAPIError={isAPIError}
                setShouldClearSearchFilters={setShouldClearSearchFilters}
                handleRetryFetch={handleLoadMore}
                handleGoBack={handleGoBack}
                isSuggestions={isSuggestions}
                isResponsive={isResponsive}
              />
            }
            noSelectionEmptyState={<SelectionEmptyState />}
            isMultiple={isMultiple}
            thumbnailLimit={assetData.length === 0 ? 20 : 5}
            localesWithFallback={localesWithFallback}
            spacesWithTitles={spacesWithTitles}
          />
        </div>

        {showSidebar && (
          <div className={`GalleryView__sidebar ${!isSidebarExpanded ? 'GalleryView__sidebar--collapsed' : ''}`}>
            {isSidebarExpanded && (
              <AssetInfoSidebar
                localesWithFallback={localesWithFallback}
                selectedAssets={spaceAssetsPicked}
                totalAssets={totalCounts}
                assetTypeSchemaMap={assetTypeSchemaData}
                userDisplayNameMap={userDisplayNameMap}
                spacesWithTitles={spacesWithTitles}
              />
            )}
          </div>
        )}

        {showSidebar && (
          <div className="GalleryView__toggle-container">
            <button
              className="GalleryView__toggle-button"
              onClick={toggleSidebarExpansion}
              aria-label={isSidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
              type="button"
            >
              <Icon
                version="v2"
                icon={isSidebarExpanded ? 'CaretRight' : 'CaretLeft'}
                size="medium"
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(GalleryView);